/**
 * The arithmetic behind the Overview's two drawings. Pure, so it can be tested without a DOM.
 *
 * ⚠️ SEPARATE FROM THE COMPONENT DELIBERATELY. A bar that is subtly wrong looks fine; the only
 * way to know it is right is to assert it, and a percentage buried in JSX cannot be asserted.
 */

export interface Segment {
  key: string;
  value: number;
  /** Percent of the track, already clamped so the row never overflows. */
  pct: number;
}

/**
 * Lay parts along a track of `total`.
 *
 * 🔴 A NON-ZERO PART IS NEVER INVISIBLE. One graded submission out of 266 is 0.38% — under a
 * pixel — and a bar that renders it as nothing says "none", which is the opposite of what the
 * number means. Anything above zero gets a floor, and the floors are taken out of the largest
 * segment so the row still sums to at most 100.
 * ⚠️ `total` IS THE DENOMINATOR, NOT THE SUM OF THE PARTS. The parts are what we graded; the
 * total is what Classroom says was submitted, so the leftover is real and must stay empty.
 */
export function segments(parts: { key: string; value: number }[], total: number,
                         minPct = 1.5): Segment[] {
  const clean = parts.map((p) => ({ ...p, value: Math.max(0, p.value || 0) }));
  if (total <= 0) return clean.map((p) => ({ ...p, pct: 0 }));

  const raw = clean.map((p) => ({ ...p, pct: (p.value / total) * 100 }));
  let owed = 0;
  for (const s of raw) {
    if (s.value > 0 && s.pct < minPct) { owed += minPct - s.pct; s.pct = minPct; }
  }
  // Take the floors back from the widest segment, never from one we just floored.
  const payers = raw.filter((s) => s.pct > minPct).sort((a, b) => b.pct - a.pct);
  for (const s of payers) {
    if (owed <= 0) break;
    const give = Math.min(owed, s.pct - minPct);
    s.pct -= give;
    owed -= give;
  }
  const sum = raw.reduce((a, s) => a + s.pct, 0);
  if (sum > 100) for (const s of raw) s.pct = (s.pct / sum) * 100;
  return raw.map((s) => ({ key: s.key, value: s.value, pct: Math.round(s.pct * 10) / 10 }));
}

/**
 * Bar heights for a series, as a fraction of the tallest.
 *
 * ⚠️ AN ALL-ZERO WEEK MUST NOT DIVIDE BY ZERO, and it must not draw seven full bars either:
 * a week nothing ran is a flat line, which is what a teacher needs to see when they wonder why
 * nothing has been graded.
 */
export function scaleSeries(values: number[]): number[] {
  const max = Math.max(0, ...values);
  if (max <= 0) return values.map(() => 0);
  return values.map((v) => (v <= 0 ? 0 : Math.max(0.04, v / max)));
}

/**
 * STOP before WARN, and stable within a severity.
 *
 * 🔴 AN ALARM LISTED UNDER A DECISION IS AN ALARM SOMEBODY SCROLLS PAST. The server already
 * sorts; this is the client half of the same rule, so a future change to either cannot
 * silently reorder the one list a teacher reads first.
 */
export function orderAttention<T extends { severity: string }>(items: T[]): T[] {
  const rank = (s: string) => (s === "STOP" ? 0 : s === "WARN" ? 1 : 2);
  return items
    .map((it, i) => ({ it, i }))
    .sort((a, b) => rank(a.it.severity) - rank(b.it.severity) || a.i - b.i)
    .map((x) => x.it);
}
